import type {
  WorkspaceNotice,
  ZipProgress,
} from "../model/WorkspaceStore.js";

import styles from "./ApplicationStatus.module.css";

interface ApplicationStatusProps {
  readonly notice: WorkspaceNotice | null;
  readonly zipProgress: ZipProgress | null;
  readonly busy: boolean;
  readonly dragActive: boolean;
  readonly onDismissNotice: () => void;
}

function Notice({
  notice,
  onDismiss,
}: {
  readonly notice: WorkspaceNotice;
  readonly onDismiss: () => void;
}) {
  return (
    <div
      class={`${styles.notice} ${notice.kind === "error" ? styles.noticeError : ""}`}
      role="alert"
    >
      <pre>{notice.message}</pre>
      <button type="button" onClick={onDismiss} aria-label="Dismiss message">
        ×
      </button>
    </div>
  );
}

function ZipProgressStatus({
  progress,
}: {
  readonly progress: ZipProgress;
}) {
  return (
    <div class={styles.zipProgressOverlay} role="status" aria-live="polite">
      <strong>
        Decompiling {progress.current.toLocaleString()} of{" "}
        {progress.total.toLocaleString()}
      </strong>
      <span class={styles.zipClassPath} title={progress.classPath}>
        {progress.classPath}
      </span>
      <progress
        class={styles.zipProgressBar}
        value={progress.current}
        max={progress.total}
        aria-label={`Decompiling class ${progress.current} of ${progress.total}`}
      />
    </div>
  );
}

export function ApplicationStatus({
  notice,
  zipProgress,
  busy,
  dragActive,
  onDismissNotice,
}: ApplicationStatusProps) {
  return (
    <>
      {notice !== null ? (
        <Notice notice={notice} onDismiss={onDismissNotice} />
      ) : null}
      {zipProgress !== null ? (
        <ZipProgressStatus progress={zipProgress} />
      ) : null}
      {busy ? <div class={styles.busyBar} aria-label="Working" /> : null}
      {dragActive ? <div class={styles.dropOverlay} aria-hidden="true" /> : null}
    </>
  );
}
